import { Fragment, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { api, ApiError } from '@/lib/api';

/**
 * One archived report from the previous system, drawn like a live one.
 *
 * Lives under the ARCHIVED tab (owner: "i want there to be a section called
 * archived/legacy and just keep the data there like before"). What separates
 * it from a live report is stated on the report itself — the period it covers
 * and the fact that it predates this system — because the one thing worse than
 * hiding history is letting somebody read it as today's takings.
 *
 * Read-only by nature: there is no range to re-run and nothing to export,
 * since these are the old system's own figures rather than a query.
 */

export interface LegacySummary {
  id: string;
  section: string;
  name: string;
  sourceFile: string;
  rowCount: number;
  periodFrom: string | null;
  periodTo: string | null;
  notes: string | null;
  importedAt: string;
}

interface LegacyTable extends LegacySummary {
  columns: Array<{ key: string; label: string }>;
  rows: Array<Record<string, string>>;
}

export function LegacyReportView({ id }: { id: string }) {
  const { t } = useTranslation();
  const [table, setTable] = useState<LegacyTable | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setTable(null);
    void (async () => {
      try {
        setTable(await api.get<LegacyTable>(`/reports/legacy/${id}`));
        setError(null);
      } catch (caught) {
        // Offline, or a fetch that never reached the API. (This once named
        // `common.error`, a key no bundle has — the alert read the raw key.)
        setError(caught instanceof ApiError ? caught.message : t('reports.legacy.loadFailed'));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, t]);

  if (error) {
    return (
      <p role="alert" className="text-sm text-danger">
        {error}
      </p>
    );
  }
  if (loading || !table) {
    return <p className="text-sm text-content-muted">{t('common.loading')}</p>;
  }

  return (
    <section className="flex min-h-0 flex-1 flex-col">
      <header className="pb-3">
        <h2 className="text-lg font-semibold text-content">{table.name}</h2>
        <p className="text-xs text-content-muted">
          {t('reports.legacy.archived')}
          {table.periodFrom ? ` · ${table.periodFrom} → ${table.periodTo}` : ''}
          {` · ${t('reports.legacy.rows', { times: table.rowCount })}`}
        </p>
        {/*
          The import's own findings — repaired cells, and any figure that hit
          the old system's counter ceiling. Kept WITH the table so a clamped
          number is never mistaken for a measured one.
        */}
        {table.notes ? (
          <p className="mt-2 rounded-control border border-ending/40 bg-input px-3 py-2 text-xs text-ending">
            {table.notes}
          </p>
        ) : null}
      </header>

      {/* Phone: a card per row, as everywhere else in the app. */}
      <div className="min-h-0 flex-1 space-y-2 overflow-y-auto md:hidden">
        {table.rows.map((row, index) => (
          <div key={index} className="rounded-card border border-subtle bg-raised p-3">
            <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-sm">
              {table.columns.map((col) => (
                <Fragment key={col.key}>
                  <dt className="text-xs leading-6 text-content-muted">{col.label}</dt>
                  <dd className="min-w-0 break-words text-end leading-6 text-content">
                    {row[col.key]}
                  </dd>
                </Fragment>
              ))}
            </dl>
          </div>
        ))}
      </div>

      <div className="min-h-0 flex-1 overflow-auto rounded-card border border-subtle max-md:hidden">
        <table className="w-full min-w-max border-collapse text-sm">
          <thead className="sticky top-0 bg-overlay">
            <tr className="border-b border-strong">
              {table.columns.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  className="whitespace-nowrap px-3 py-2 text-start text-[10px] font-semibold uppercase tracking-[0.14em] text-content-muted"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {table.rows.map((row, index) => (
              <tr
                key={index}
                className={`border-b border-subtle/50 ${index % 2 === 1 ? 'bg-row-odd' : 'bg-row-even'}`}
              >
                {table.columns.map((col) => (
                  <td
                    key={col.key}
                    className="whitespace-nowrap px-3 py-2 text-content max-md:px-2"
                  >
                    {row[col.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}
